import React, { useState, useEffect, useMemo } from 'react';
import './MainContent.css';
import FileUpload from './FileUpload';
import {
  createFamilyFormData,
  toFamilyData,
  calculateTax,
  formatIncomeYearLabel,
  UnsupportedTaxYearError,
  TaxCalculationWithDetails,
} from '../domain/tax';
import DetailTable from './DetailTable';
import MonthlyPayrollTable from './MonthlyPayrollTable';
import GoogleDriveBackup from './GoogleDriveBackup';
import { usePayroll } from '../context/PayrollContext';
import { prewarmBrowserLlm } from '../services/browserLlm';
import { analyzePdf } from '../services/pdfAnalysis';
import { SalarySlipData } from '../types/PayrollTypes';
import { formatAnalysisMethod } from '../utils/analysisMethod';
import { PayrollEstimation } from '../utils/payrollEstimation';

/**
 * `familyData` 未補完のセッションでも再レンダーごとに新しい既定値を作らないための固定値。
 * 通常は `PayrollContext` が非 null セッションに `familyData` を補完するため使用されない。
 */
const DEFAULT_FAMILY_FORM_DATA = createFamilyFormData();

type CalculationState =
  | { kind: 'none' }
  | { kind: 'ok'; data: TaxCalculationWithDetails }
  | { kind: 'unsupported'; year: number }
  | { kind: 'error'; message: string };

const MainContent: React.FC = () => {
  const {
    payrollState,
    initializeSession,
    addPdfData,
    addBonus,
    deleteBonus,
    updateMonthlyData,
    resetMonthlyData,
    resetAllUserEditedData,
    updateFamilyData,
    clearSession,
    restoreSession,
    setLoading,
    setError,
    changeYear
  } = usePayroll();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [analysisMeta, setAnalysisMeta] = useState<{
    confidence?: number;
    warnings: string[];
    analysisMethod?: string;
  } | null>(null);

  // セッションが存在しない場合、デフォルトで初期化
  useEffect(() => {
    if (!payrollState.currentSession) {
      initializeSession();
    }
  }, [payrollState.currentSession, initializeSession]);

  // 給与データ・家族構成・対象年からローカルで税額を計算する（サーバ往復なし）
  const calculationState = useMemo<CalculationState>(() => {
    const session = payrollState.currentSession;
    if (!session || session.monthlyData.length === 0) {
      return { kind: 'none' };
    }

    const totals = PayrollEstimation.calculateAnnualTotals(session.monthlyData, session.currentYear);
    try {
      // UI 入力（配偶者年収）を選択年分のルールで合計所得へ換算してからエンジンへ渡す
      // familyData は session を単一の真実源とする（未補完時のみ既定値へフォールバック）。
      const familyData = toFamilyData(
        session.familyData ?? DEFAULT_FAMILY_FORM_DATA,
        session.currentYear,
      );
      const data = calculateTax({
        annualIncome: totals.totalGrossSalary,
        healthInsurance: totals.totalHealthInsurance,
        employmentInsurance: totals.totalEmploymentInsurance,
        nationalPension: totals.totalNationalPension,
        definedContribution: totals.totalDefinedContribution,
        familyData,
        taxYear: session.currentYear,
      });
      return { kind: 'ok', data };
    } catch (error) {
      if (error instanceof UnsupportedTaxYearError) {
        return { kind: 'unsupported', year: error.taxYear };
      }
      return {
        kind: 'error',
        message: error instanceof Error ? error.message : '税額計算に失敗しました',
      };
    }
  }, [payrollState.currentSession]);

  const handleFileUpload = async (file: File) => {
    // ファイル選択直後（transient activation が有効な間）に内蔵LLMセッションを先行生成する（MI-2）
    prewarmBrowserLlm();
    setUploadedFile(file);
    setIsAnalyzing(true);
    setAnalyzeError(null);
    setAnalysisMeta(null);
    setLoading(true);

    try {
      // PDF解析: ルール → 端末内AI → サーバAI の順で経路を判定する（services/pdfAnalysis）
      const salaryData: SalarySlipData = await analyzePdf(file);

      setAnalysisMeta({
        confidence: salaryData.confidence,
        warnings: salaryData.warnings ?? [],
        analysisMethod: salaryData.analysisMethod,
      });

      // 月別給与データに追加
      if (payrollState.currentSession) {
        addPdfData(salaryData);
      }
    } catch (error) {
      console.error('ファイル解析エラー:', error);
      const errorMessage = error instanceof Error ? error.message : 'ファイルの解析に失敗しました';
      setAnalyzeError(errorMessage);
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
      setLoading(false);
    }
  };

  return (
    <main className="main-content">
      <div className="container">
        <section className="upload-section">
          <h2>給与明細をアップロード</h2>
          <FileUpload onFileUpload={handleFileUpload} />
          {uploadedFile && (
            <div className="upload-success">
              <p>✓ {uploadedFile.name} がアップロードされました</p>
              {isAnalyzing && <p>📊 ファイルを解析中...</p>}
              {analyzeError && <p style={{color: '#dc3545'}}>❌ {analyzeError}</p>}
              {analysisMeta && (
                <div className="analysis-meta">
                  <p>
                    解析方法: {formatAnalysisMethod(analysisMeta.analysisMethod)}
                    {' / '}
                    解析信頼度: {analysisMeta.confidence !== undefined ? analysisMeta.confidence.toFixed(2) : '-'}
                  </p>
                  {analysisMeta.warnings.length > 0 && (
                    <ul className="analysis-warnings">
                      {analysisMeta.warnings.map((warning, index) => (
                        <li key={index}>⚠ {warning}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* 月別給与明細表 */}
        {payrollState.currentSession && (
          <section className="monthly-payroll-section">
            {payrollState.error && (
              <div className="error-message" style={{color: '#dc3545', margin: '10px 0'}}>
                ❌ {payrollState.error}
              </div>
            )}
            <div className="monthly-payroll-wrapper">
              <MonthlyPayrollTable
              session={payrollState.currentSession}
              onDataUpdate={updateMonthlyData}
              onAddBonus={addBonus}
              onDeleteBonus={deleteBonus}
              onResetMonthlyData={resetMonthlyData}
              onResetAllUserEdited={resetAllUserEditedData}
              onChangeYear={(year) => changeYear(year)}
              onClearSession={() => {
                if (window.confirm('すべてのデータをクリアして新しいセッションを開始しますか？')) {
                  clearSession();
                  initializeSession();
                }
              }}
              familyData={payrollState.currentSession.familyData ?? DEFAULT_FAMILY_FORM_DATA}
              onFamilyDataChange={updateFamilyData}
            />
            </div>
          </section>
        )}

        {/* Google Driveバックアップセクション */}
        {payrollState.currentSession && (
          <section className="backup-section-wrapper">
            <GoogleDriveBackup 
              session={payrollState.currentSession}
              onDataRestore={restoreSession}
            />
          </section>
        )}

        <section className="calculation-section">
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>計算結果</h2>
            {calculationState.kind === 'ok' && (
              <p className="calculation-meta">
                適用年分: {formatIncomeYearLabel(calculationState.data.taxYear)}
                （住民税: {calculationState.data.residentFiscalYear}年度）
                <br />
                ※ 計算結果は概算です。税務上の最終判断は利用者または専門家にご相談ください。
              </p>
            )}
            {calculationState.kind === 'unsupported' && (
              <p className="calculation-meta" style={{ color: '#dc3545' }}>
                {calculationState.year}年分は未対応です。2024〜2027年分のいずれかを選択してください。
              </p>
            )}
            {calculationState.kind === 'error' && (
              <p className="calculation-meta" style={{ color: '#dc3545' }}>
                ❌ {calculationState.message}
              </p>
            )}
          </div>
          <DetailTable data={calculationState.kind === 'ok' ? calculationState.data : null} />
        </section>
      </div>
    </main>
  );
};

export default MainContent;
